import { useEffect, useRef, useCallback, RefObject } from 'react';
import { WebsitePage } from '../api/cms.api';

interface VvvebBridgeOptions {
    token: string;
    saveEndpoint?: string;
    onReady?: () => void;
    onSaveTriggered?: () => void;
    onSaveSuccess?: (data: unknown) => void;
    onSaveError?: (error: { message: string }) => void;
    onPageLoaded?: (payload: { url: string; subpageId?: number }) => void;
    onNewPageCreated?: (payload: { name: string; title: string; url: string; blobUrl: string; tempId: number }) => void;
    onUploadImage?: (payload: { uploadId: string; filename: string; mimeType: string; data: string }) => void;
    onPageDeleted?: (payload: { subpageId: number | undefined; name: string }) => void;
    onPageUpdated?: (payload: { subpageId: number | undefined; title: string; pageUrl: string }) => void;
}

interface VvvebBridgeReturn {
    iframeRef: RefObject<HTMLIFrameElement>;
    triggerSave: () => void;
    loadPage: (url: string) => void;
    getHtml: () => Promise<string>;
    setEditorPages: (pages: WebsitePage[], defaultPageUrl?: string) => void;
    resolveImageUpload: (uploadId: string, url: string | null, error?: string) => void;
}

let _pendingHtmlResolve: ((html: string) => void) | null = null;

export function useVvvebBridge({
    token,
    saveEndpoint,
    onReady,
    onSaveTriggered,
    onSaveSuccess,
    onSaveError,
    onPageLoaded,
    onNewPageCreated,
    onUploadImage,
    onPageDeleted,
    onPageUpdated,
}: VvvebBridgeOptions): VvvebBridgeReturn {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const readyRef = useRef(false);

    const onReadyRef = useRef(onReady);
    const onSaveTriggeredRef = useRef(onSaveTriggered);
    const onSaveSuccessRef = useRef(onSaveSuccess);
    const onSaveErrorRef = useRef(onSaveError);
    const onPageLoadedRef = useRef(onPageLoaded);
    const onNewPageCreatedRef = useRef(onNewPageCreated);
    const onUploadImageRef = useRef(onUploadImage);
    const onPageDeletedRef = useRef(onPageDeleted);
    const onPageUpdatedRef = useRef(onPageUpdated);

    useEffect(() => {
        onReadyRef.current = onReady;
    }, [ onReady ]);
    useEffect(() => {
        onSaveTriggeredRef.current = onSaveTriggered;
    }, [ onSaveTriggered ]);
    useEffect(() => {
        onSaveSuccessRef.current = onSaveSuccess;
    }, [ onSaveSuccess ]);
    useEffect(() => {
        onSaveErrorRef.current = onSaveError;
    }, [ onSaveError ]);
    useEffect(() => {
        onPageLoadedRef.current = onPageLoaded;
    }, [ onPageLoaded ]);
    useEffect(() => {
        onNewPageCreatedRef.current = onNewPageCreated;
    }, [ onNewPageCreated ]);
    useEffect(() => {
        onUploadImageRef.current = onUploadImage;
    }, [ onUploadImage ]);
    useEffect(() => {
        onPageDeletedRef.current = onPageDeleted;
    }, [ onPageDeleted ]);
    useEffect(() => {
        onPageUpdatedRef.current = onPageUpdated;
    }, [ onPageUpdated ]);

    const send = useCallback((type: string, payload: Record<string, unknown> = {}) => {
        iframeRef.current?.contentWindow?.postMessage(
            { type, payload },
            window.location.origin,
        );
    }, []);

    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) {
                return;
            }

            const { type, payload } = (event.data || {}) as {
                type: string;
                payload: Record<string, unknown>;
            };

            switch (type) {
                case 'VVVEB_READY':
                    readyRef.current = true;
                    send('VVVEB_SET_SAVE_CONFIG', { endpoint: saveEndpoint, token });
                    onReadyRef.current?.();
                    break;

                case 'VVVEB_SAVE_TRIGGERED':
                    onSaveTriggeredRef.current?.();
                    break;

                case 'VVVEB_SAVE_SUCCESS':
                    onSaveSuccessRef.current?.(payload);
                    break;

                case 'VVVEB_SAVE_ERROR':
                    onSaveErrorRef.current?.(payload as { message: string });
                    break;

                case 'VVVEB_PAGE_LOADED':
                    onPageLoadedRef.current?.(payload as { url: string; subpageId?: number });
                    break;

                case 'VVVEB_NEW_PAGE_CREATED':
                    onNewPageCreatedRef.current?.(payload as {
                        name: string; title: string; url: string; blobUrl: string; tempId: number
                    });
                    break;

                case 'VVVEB_UPLOAD_IMAGE':
                    onUploadImageRef.current?.(payload as {
                        uploadId: string; filename: string; mimeType: string; data: string
                    });
                    break;

                case 'VVVEB_PAGE_DELETED':
                    onPageDeletedRef.current?.(payload as { subpageId: number | undefined; name: string });
                    break;

                case 'VVVEB_PAGE_UPDATED':
                    onPageUpdatedRef.current?.(payload as {
                        subpageId: number | undefined; title: string; pageUrl: string
                    });
                    break;

                case 'VVVEB_HTML_RESULT':
                    _pendingHtmlResolve?.(payload.html as string);
                    _pendingHtmlResolve = null;
                    break;
            }
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [ send, saveEndpoint, token ]);

    useEffect(() => {
        if (readyRef.current) {
            send('VVVEB_SET_SAVE_CONFIG', { endpoint: saveEndpoint, token });
        }
    }, [ token, saveEndpoint, send ]);

    const triggerSave = useCallback(() => {
        send('VVVEB_TRIGGER_SAVE');
    }, [ send ]);

    const loadPage = useCallback((url: string) => {
        send('VVVEB_LOAD_PAGE', { url });
    }, [ send ]);

    const getHtml = useCallback((): Promise<string> => {
        return new Promise((resolve) => {
            _pendingHtmlResolve = resolve;
            send('VVVEB_GET_HTML');
        });
    }, [ send ]);

    const setEditorPages = useCallback(
        (pages: WebsitePage[], defaultPageUrl?: string) => {
            send('VVVEB_SET_PAGES', {
                pages,
                defaultPageUrl: defaultPageUrl ?? pages[0]?.url ?? '',
            });
        },
        [ send ],
    );

    const resolveImageUpload = useCallback((uploadId: string, url: string | null, error?: string) => {
        send('VVVEB_UPLOAD_RESULT', { uploadId, url, error: error ?? null });
    }, [ send ]);

    return { iframeRef, triggerSave, loadPage, getHtml, setEditorPages, resolveImageUpload };
}
